import { useState } from 'react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Mail, Phone, MapPin, Send, Instagram, Music2, MessageCircle, Clock } from 'lucide-react';

export default function Contact() {
  const [f, setF] = useState({ name: '', email: '', subject: '', message: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/contact', f);
      toast.success('Message envoyé — nous vous répondrons rapidement');
      setF({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur, réessayez');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-12">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs font-semibold uppercase tracking-widest">
            Contact
          </div>
          <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl">Parlons ensemble</h1>
          <p className="mt-3 text-slate-300 max-w-2xl">
            Une question, un partenariat, un souci ? L'équipe JOTALY vous répond depuis Dakar.
          </p>
        </div>

        <div className="grid md:grid-cols-[1.4fr_1fr] gap-8">
          {/* Form */}
          <form onSubmit={submit} className="card-elev rounded-3xl p-6 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <input required data-testid="contact-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                placeholder="Votre nom"
                className="px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:border-sky-400 outline-none" />
              <input required type="email" data-testid="contact-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                placeholder="Email"
                className="px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:border-sky-400 outline-none" />
            </div>
            <input required data-testid="contact-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })}
              placeholder="Sujet"
              className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:border-sky-400 outline-none" />
            <textarea required rows={6} data-testid="contact-message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })}
              placeholder="Votre message…"
              className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:border-sky-400 outline-none" />
            <Button disabled={busy} type="submit" data-testid="contact-submit"
              className="btn-brand text-white px-6 py-6 rounded-xl font-bold">
              <Send className="h-4 w-4 mr-2" />{busy ? 'Envoi…' : 'Envoyer le message'}
            </Button>
          </form>

          {/* Info */}
          <div className="space-y-4">
            <div className="card-elev rounded-2xl p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Nous joindre</div>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="h-9 w-9 grid place-items-center rounded-xl bg-rose-500/15 text-rose-400"><Mail className="h-4 w-4" /></span>
                  <div>
                    <div className="text-slate-500 text-xs">Email</div>
                    <a href="mailto:contactjotaly@gmail.com" className="text-slate-100 hover:text-rose-400">contactjotaly@gmail.com</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-9 w-9 grid place-items-center rounded-xl bg-rose-500/15 text-rose-400"><Phone className="h-4 w-4" /></span>
                  <div>
                    <div className="text-slate-500 text-xs">Téléphone / WhatsApp</div>
                    <a href="tel:+33783170963" className="text-slate-100 hover:text-rose-400 font-mono-num">+33 7 83 17 09 63</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-9 w-9 grid place-items-center rounded-xl bg-rose-500/15 text-rose-400"><MapPin className="h-4 w-4" /></span>
                  <div>
                    <div className="text-slate-500 text-xs">Adresse</div>
                    <div className="text-slate-100">Dakar, Sénégal</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-9 w-9 grid place-items-center rounded-xl bg-rose-500/15 text-rose-400"><Clock className="h-4 w-4" /></span>
                  <div>
                    <div className="text-slate-500 text-xs">Horaires</div>
                    <div className="text-slate-100">Lun–Sam · 9h – 20h GMT</div>
                  </div>
                </li>
              </ul>
            </div>
            <div className="card-elev rounded-2xl p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Suivez-nous</div>
              <div className="mt-3 flex items-center gap-2">
                <a href="https://instagram.com" target="_blank" rel="noreferrer" className="h-10 w-10 grid place-items-center rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:border-rose-500/50 hover:text-white"><Instagram className="h-4 w-4" /></a>
                <a href="https://tiktok.com" target="_blank" rel="noreferrer" className="h-10 w-10 grid place-items-center rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:border-rose-500/50 hover:text-white"><Music2 className="h-4 w-4" /></a>
                <a href="https://wa.me/33783170963" target="_blank" rel="noreferrer" className="h-10 w-10 grid place-items-center rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:border-rose-500/50 hover:text-white"><MessageCircle className="h-4 w-4" /></a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
